import {
  Input,
  Space,
  Typography,
  ConfigProvider,
  Flex,
  Button,
  Divider,
} from "antd";
import { SearchOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { useTranslate } from "#src/hooks";
import { FontCategory, FontItem } from "../types";
import { FontCategorySection } from "./FontCategorySection";
import { useAntTheme } from "hooks/useAntTheme";

const UploadIcon = () => {
  const { theme } = useAntTheme();
  return (
    <Flex
      align="center"
      justify="center"
      style={{
        backgroundColor: theme.colorPrimary,
        width: 25,
        height: 25,
        borderRadius: theme.borderRadiusLG,
        color: theme.colorBgBase,
      }}
    >
      <CloudUploadOutlined />
    </Flex>
  );
};

interface FontDropdownContentProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  fontCategories: FontCategory[];
  expandedFamilies: Set<string>;
  selectedFont?: string;
  selectedBackgroundColor: string;
  onFontSelect: (fontItem: FontItem) => void;
  onFamilyToggle: (familyName: string, keys: string[]) => void;
  onUploadFont?: () => void;
  onDropdownClose: () => void;
  assetLibraryId: string;
  onFontDeleteSuccess?: (fontItemId: string) => void;
}

export const FontDropdownContent = ({
  searchTerm,
  onSearchChange,
  fontCategories,
  expandedFamilies,
  selectedFont,
  selectedBackgroundColor,
  onFontSelect,
  onFamilyToggle,
  onUploadFont,
  onDropdownClose,
  assetLibraryId,
  onFontDeleteSuccess,
}: FontDropdownContentProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  return (
    <div data-testid="font-dropdown-content" data-interactive>
      <Flex
        vertical
        style={{
          width: "calc(100% - 48px)",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          padding: "16px 24px 0 24px",
        }}
        gap={10}
      >
        <Typography.Text strong style={{ fontSize: 14 }}>
          {t("components.FontSelect.fonts")}
        </Typography.Text>
        <Input
          placeholder={t("components.FontSelect.searchPlaceholder")}
          prefix={<SearchOutlined />}
          allowClear
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          size="large"
        />
      </Flex>

      <div
        style={{
          maxHeight: "calc(100vh - 330px)",
          overflowY: "auto",
          marginTop: 20,
        }}
      >
        <Space direction="vertical" size={0} style={{ width: "100%" }}>
          <ConfigProvider
            theme={{
              components: {
                Collapse: {
                  headerPadding: "12px 0 12px 0",
                  fontSize: 14,
                },
              },
            }}
          >
            {fontCategories.map((category, index) => (
              <div key={category.categoryName}>
                <div style={{ padding: "0 24px" }}>
                  <FontCategorySection
                    category={category}
                    expandedFamilies={expandedFamilies}
                    selectedFont={selectedFont}
                    selectedBackgroundColor={selectedBackgroundColor}
                    onFontSelect={onFontSelect}
                    onFamilyToggle={onFamilyToggle}
                    assetLibraryId={assetLibraryId}
                    onFontDeleteSuccess={onFontDeleteSuccess}
                  />
                </div>
                {index < fontCategories.length - 1 && (
                  <Divider
                    style={{
                      margin: "20px 0 16px 0",
                      borderColor: theme.colorBorder,
                    }}
                  />
                )}
              </div>
            ))}
          </ConfigProvider>
          {fontCategories.length === 0 && (
            <Flex
              justify="center"
              style={{ padding: "20px 24px", color: theme.colorTextDisabled }}
            >
              {searchTerm
                ? t("components.FontSelect.noFontsFound", { searchTerm })
                : t("components.FontSelect.noFontsAvailable")}
            </Flex>
          )}
        </Space>
      </div>

      {onUploadFont && (
        <div style={{ padding: "0 24px 24px 24px" }}>
          <Button
            data-testid="font-upload-button"
            size="large"
            onClick={(e) => {
              e.stopPropagation();
              onDropdownClose();
              onUploadFont();
            }}
            style={{ fontSize: 14, width: "100%", marginTop: 16 }}
            icon={<UploadIcon />}
          >
            {t("components.FontSelect.uploadMyFont")}
          </Button>
        </div>
      )}
    </div>
  );
};
